import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  IconButton,
  Alert,
  Grid,
  FormControlLabel,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAdminFetch } from '../hooks/useAdminFetch';
import {
  DateRangePicker,
  DatePeriod,
  OverviewCards,
  RealtimeWidget,
  PageViewsChart,
  TopPagesTable,
  ReferrersChart,
  DevicesChart,
  EventsTable,
} from '../components/analytics';

interface OverviewData {
  total_sessions: number;
  unique_visitors: number;
  total_page_views: number;
  bounce_rate: number;
  avg_session_duration: number;
  changes: {
    sessions: number;
    visitors: number;
    page_views: number;
  };
}

interface PageViewData {
  date: string;
  views: number;
}

interface PageData {
  path: string;
  views: number;
  unique_sessions: number;
}

interface ReferrerData {
  referrer_domain: string;
  sessions: number;
  visitors: number;
}

interface DevicesData {
  devices: Array<{ device_type: string; count: number }>;
  browsers: Array<{ browser: string; count: number }>;
  operating_systems: Array<{ os: string; count: number }>;
}

interface EventsData {
  summary: Array<{ event_category: string; event_name: string; count: number }>;
  recent: Array<{
    event_name: string;
    event_category: string;
    event_data: Record<string, unknown> | null;
    page_path: string | null;
    timestamp: string;
    event_value: number | null;
  }>;
}

const AdminAnalytics: React.FC = () => {
  const navigate = useNavigate();
  const adminFetch = useAdminFetch();
  const apiUrl = process.env.REACT_APP_API_URL;

  const [period, setPeriod] = useState<DatePeriod>('7d');
  const [authenticatedOnly, setAuthenticatedOnly] = useState(false);
  const [includeStaff, setIncludeStaff] = useState(false);
  const [botFilter, setBotFilter] = useState<'exclude' | 'only' | 'include'>('exclude');
  const [error, setError] = useState<string | null>(null);

  // Data states
  const [overviewData, setOverviewData] = useState<OverviewData | null>(null);
  const [pageViewsData, setPageViewsData] = useState<PageViewData[]>([]);
  const [pagesData, setPagesData] = useState<PageData[]>([]);
  const [referrersData, setReferrersData] = useState<ReferrerData[]>([]);
  const [devicesData, setDevicesData] = useState<DevicesData | null>(null);
  const [eventsData, setEventsData] = useState<EventsData | null>(null);

  // Loading states
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingPageViews, setLoadingPageViews] = useState(true);
  const [loadingPages, setLoadingPages] = useState(true);
  const [loadingReferrers, setLoadingReferrers] = useState(true);
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(true);

  const buildQueryParams = useCallback(() => {
    const params = new URLSearchParams();
    params.set('period', period);
    if (authenticatedOnly) {
      params.set('authenticated', 'true');
    }
    if (includeStaff) {
      params.set('include_staff', 'true');
    }
    params.set('bots', botFilter);
    return params.toString();
  }, [period, authenticatedOnly, includeStaff, botFilter]);

  const fetchAllData = useCallback(async () => {
    const queryString = buildQueryParams();
    setError(null);

    // Fetch overview
    setLoadingOverview(true);
    try {
      const overview = await adminFetch(`${apiUrl}/api/admin/analytics/overview/?${queryString}`);
      setOverviewData(overview);
    } catch (err) {
      console.error('Failed to fetch overview:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoadingOverview(false);
    }

    // Fetch page views
    setLoadingPageViews(true);
    try {
      const pageViews = await adminFetch(`${apiUrl}/api/admin/analytics/pageviews/?${queryString}`);
      setPageViewsData(pageViews.data || []);
    } catch (err) {
      console.error('Failed to fetch page views:', err);
    } finally {
      setLoadingPageViews(false);
    }

    // Fetch pages
    setLoadingPages(true);
    try {
      const pages = await adminFetch(`${apiUrl}/api/admin/analytics/pages/?${queryString}`);
      setPagesData(pages.pages || []);
    } catch (err) {
      console.error('Failed to fetch pages:', err);
    } finally {
      setLoadingPages(false);
    }

    // Fetch referrers
    setLoadingReferrers(true);
    try {
      const referrers = await adminFetch(`${apiUrl}/api/admin/analytics/referrers/?${queryString}`);
      setReferrersData(referrers.referrers || []);
    } catch (err) {
      console.error('Failed to fetch referrers:', err);
    } finally {
      setLoadingReferrers(false);
    }

    // Fetch devices
    setLoadingDevices(true);
    try {
      const devices = await adminFetch(`${apiUrl}/api/admin/analytics/devices/?${queryString}`);
      setDevicesData(devices);
    } catch (err) {
      console.error('Failed to fetch devices:', err);
    } finally {
      setLoadingDevices(false);
    }

    // Fetch events
    setLoadingEvents(true);
    try {
      const events = await adminFetch(`${apiUrl}/api/admin/analytics/events/?${queryString}`);
      setEventsData(events);
    } catch (err) {
      console.error('Failed to fetch events:', err);
    } finally {
      setLoadingEvents(false);
    }
  }, [adminFetch, apiUrl, buildQueryParams]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleRefresh = () => {
    fetchAllData();
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 5, mb: 5 }}>
      <Box className="page-header">
        <Box className="title-row" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => navigate('/admin')} aria-label="back to admin">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1" className="page-title">
              Analytics
            </Typography>
          </Box>
          <IconButton onClick={handleRefresh} aria-label="refresh data">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3, flexWrap: 'wrap' }}>
        <DateRangePicker period={period} onChange={setPeriod} />
        <FormControlLabel
          control={
            <Switch
              checked={authenticatedOnly}
              onChange={(e) => setAuthenticatedOnly(e.target.checked)}
              size="small"
            />
          }
          label="Logged-in users only"
        />
        <FormControlLabel
          control={
            <Switch
              checked={includeStaff}
              onChange={(e) => setIncludeStaff(e.target.checked)}
              size="small"
            />
          }
          label="Include staff/admins"
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">Bots:</Typography>
          <ToggleButtonGroup
            value={botFilter}
            exclusive
            onChange={(_, value) => { if (value) setBotFilter(value); }}
            size="small"
          >
            <ToggleButton value="exclude">Humans</ToggleButton>
            <ToggleButton value="include">All</ToggleButton>
            <ToggleButton value="only">Bots</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={9}>
          <OverviewCards data={overviewData} loading={loadingOverview} />
          <PageViewsChart data={pageViewsData} loading={loadingPageViews} period={period} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TopPagesTable data={pagesData} loading={loadingPages} />
            </Grid>
            <Grid item xs={12} md={6}>
              <ReferrersChart data={referrersData} loading={loadingReferrers} />
            </Grid>
          </Grid>

          <DevicesChart data={devicesData} loading={loadingDevices} />
          <EventsTable data={eventsData} loading={loadingEvents} />
        </Grid>

        <Grid item xs={12} md={3}>
          <RealtimeWidget />
        </Grid>
      </Grid>
    </Container>
  );
};

export default AdminAnalytics;
